import React, { useEffect, useState } from 'react';
import {
  Alert, Box, Button, CircularProgress, MenuItem, Paper, Stack,
  Table, TableBody, TableCell, TableHead, TableRow, TextField, Typography,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { adminAPI, recordingAPI } from '../../utils/api';
import { formatToBogotaTime } from '../../utils/dateUtils';
import VideoPlayer from '../common/VideoPlayer';

const active = (run) => run && ['pending', 'running'].includes(run.status);

const EvaluationLab = () => {
  const { t } = useTranslation();
  const label = (key) => t(`admin.evaluationLab.${key}`);
  const [config, setConfig] = useState(null);
  const [candidates, setCandidates] = useState([]);
  const [candidateId, setCandidateId] = useState('');
  const [assignments, setAssignments] = useState([]);
  const [assignmentId, setAssignmentId] = useState('');
  const [recordingId, setRecordingId] = useState('');
  const [model, setModel] = useState('');
  const [prompt, setPrompt] = useState('');
  const [history, setHistory] = useState({ items: [], total: 0 });
  const [offset, setOffset] = useState(0);
  const [runId, setRunId] = useState(null);
  const [run, setRun] = useState(null);
  const [refresh, setRefresh] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  const assignment = assignments.find((item) => item.assignment_id === assignmentId);
  const question = assignment?.questions.find((item) => item.recording.id === recordingId);

  useEffect(() => {
    let cancelled = false;
    Promise.all([adminAPI.getEvaluationConfig(), adminAPI.getCandidatesForGrading()])
      .then(([defaults, data]) => {
        if (cancelled) return;
        setConfig(defaults);
        setModel(defaults.model);
        setPrompt(defaults.template);
        setCandidates(data.candidates);
      }).catch((err) => { if (!cancelled) setError(err.message); });
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    let cancelled = false;
    setAssignments([]);
    setAssignmentId('');
    setRecordingId('');
    if (candidateId) {
      adminAPI.getCandidateRecordings(candidateId).then((data) => {
        if (!cancelled) setAssignments(data.assignments);
      }).catch((err) => { if (!cancelled) setError(err.message); });
    }
    return () => { cancelled = true; };
  }, [candidateId]);

  useEffect(() => {
    setRunId(null);
    setRun(null);
    setOffset(0);
    setHistory({ items: [], total: 0 });
  }, [recordingId]);

  useEffect(() => {
    if (!recordingId) return undefined;
    let cancelled = false;
    let timer;
    const load = async () => {
      try {
        const [data, detail] = await Promise.all([
          adminAPI.getEvaluationRuns(recordingId, offset),
          runId ? adminAPI.getEvaluationRun(runId) : Promise.resolve(null),
        ]);
        if (cancelled) return;
        setHistory(data);
        setRun(detail);
        if (data.items.some(active) || active(detail)) timer = setTimeout(load, 3000);
      } catch (err) {
        if (!cancelled) setError(err.message);
      }
    };
    load();
    return () => { cancelled = true; clearTimeout(timer); };
  }, [recordingId, offset, runId, refresh]);

  const submit = async () => {
    setSubmitting(true);
    setError('');
    try {
      const created = await adminAPI.createEvaluationRun({ recording_id: recordingId, model, system_prompt: prompt });
      setOffset(0);
      setRun(null);
      setRunId(created.id);
      setRefresh((value) => value + 1);
    } catch (err) {
      setError(err.message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Stack spacing={3}>
      <Typography variant="h5">{label('title')}</Typography>
      <Alert severity="info">{label('description')}</Alert>
      {error && <Alert severity="error" onClose={() => setError('')}>{error}</Alert>}
      {!config && !error && <CircularProgress aria-label={label('loading')} />}
      {config && !config.available && <Alert severity="warning">{label('unavailable')}</Alert>}
      <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
        <TextField select fullWidth label={label('candidate')} value={candidateId} disabled={submitting}
          onChange={(event) => setCandidateId(event.target.value)}>
          {candidates.map((item) => <MenuItem key={item.id} value={item.id}>{item.email}</MenuItem>)}
        </TextField>
        <TextField select fullWidth label={label('assessment')} value={assignmentId} disabled={submitting || !assignments.length}
          onChange={(event) => { setAssignmentId(event.target.value); setRecordingId(''); }}>
          {assignments.map((item) => <MenuItem key={item.assignment_id} value={item.assignment_id}>{item.assessment_name}</MenuItem>)}
        </TextField>
        <TextField select fullWidth label={label('response')} value={recordingId} disabled={submitting || !assignment}
          onChange={(event) => setRecordingId(event.target.value)}>
          {(assignment?.questions || []).map((item, index) => <MenuItem key={item.recording.id} value={item.recording.id}>
            {index + 1}. {item.question_text}
          </MenuItem>)}
        </TextField>
      </Stack>
      {candidateId && !assignments.length && <Typography>{label('noResponses')}</Typography>}
      {question && <Paper variant="outlined" sx={{ p: 2 }}>
        <Typography variant="h6">{question.question_text}</Typography>
        <Typography sx={{ whiteSpace: 'pre-wrap', my: 1 }}>{question.recording.comments || label('noWrittenResponse')}</Typography>
        {question.recording.video_ready
          ? <VideoPlayer key={recordingId} url={recordingAPI.getScreenRecording(recordingId)} title={label('video')} />
          : <Alert severity="warning">{label('videoNotReady')}</Alert>}
        {question.grade && <Typography>{label('officialGrade')}: {question.grade.score_percentage}%</Typography>}
      </Paper>}
      <TextField label={label('model')} value={model} onChange={(event) => setModel(event.target.value)}
        helperText={label('modelHelp')} disabled={!config || submitting} />
      <Stack direction="row" spacing={2}>
        <Button onClick={() => setPrompt(config.template)} disabled={!config || submitting}>{label('loadDefault')}</Button>
        <Button onClick={() => setPrompt(assignment.system_prompt)} disabled={!assignment?.system_prompt || submitting}>{label('loadAssessment')}</Button>
      </Stack>
      <TextField label={label('prompt')} multiline minRows={12} maxRows={30} value={prompt}
        onChange={(event) => setPrompt(event.target.value)} helperText={label('promptHelp')} disabled={!config || submitting} />
      <Box>
        <Button variant="contained" onClick={submit}
          disabled={submitting || !config?.available || !question?.recording.video_ready || !model.trim() || !prompt.trim()}>
          {submitting ? label('submitting') : label('run')}
        </Button>
      </Box>
      {recordingId && <Paper variant="outlined" sx={{ p: 2, overflowX: 'auto' }}>
        <Stack direction="row" justifyContent="space-between">
          <Typography variant="h6">{label('history')}</Typography>
          <Button onClick={() => setRefresh((value) => value + 1)}>{label('refresh')}</Button>
        </Stack>
        {!history.items.length && <Typography>{label('noRuns')}</Typography>}
        <Table size="small">
          <TableHead><TableRow>
            <TableCell>{label('created')}</TableCell><TableCell>{label('model')}</TableCell>
            <TableCell>{label('status')}</TableCell><TableCell>{label('score')}</TableCell><TableCell />
          </TableRow></TableHead>
          <TableBody>{history.items.map((item) => <TableRow key={item.id} selected={runId === item.id}>
            <TableCell>{formatToBogotaTime(item.created_at)}</TableCell><TableCell>{item.model}</TableCell>
            <TableCell>{label(`statuses.${item.status}`)}</TableCell><TableCell>{item.score == null ? '—' : `${item.score}%`}</TableCell>
            <TableCell><Button onClick={() => { setRun(null); setRunId(item.id); }}>{label('open')} #{item.id}</Button></TableCell>
          </TableRow>)}</TableBody>
        </Table>
        <Stack direction="row" spacing={2}>
          <Button disabled={offset === 0} onClick={() => setOffset(offset - 20)}>{label('previous')}</Button>
          <Button disabled={offset + 20 >= history.total} onClick={() => setOffset(offset + 20)}>{label('next')}</Button>
        </Stack>
      </Paper>}
      {run && <Paper variant="outlined" sx={{ p: 2, overflowWrap: 'anywhere' }}>
        <Typography variant="h6">{label('evaluation')} #{run.id} — {label(`statuses.${run.status}`)}</Typography>
        {active(run) && <Alert severity="info">{label('running')}</Alert>}
        {run.error && <Alert severity="error">{run.error}</Alert>}
        {run.recorded_at !== question?.recording.recorded_at && <Alert severity="warning">{label('sourceChanged')}</Alert>}
        <Typography>{label('model')}: {run.model_version || run.model}</Typography>
        <Typography>{label('created')}: {formatToBogotaTime(run.created_at)}</Typography>
        {run.started_at && <Typography>{label('started')}: {formatToBogotaTime(run.started_at)}</Typography>}
        {run.completed_at && <Typography>{label('completed')}: {formatToBogotaTime(run.completed_at)}</Typography>}
        {run.score != null && <Typography variant="h5">{label('score')}: {run.score}%</Typography>}
        {run.tokens_used != null && <Typography>{label('tokens')}: {run.tokens_used}</Typography>}
        <ReactMarkdown remarkPlugins={[remarkGfm]}>{run.feedback || ''}</ReactMarkdown>
        <Typography variant="subtitle1">{label('savedQuestion')}</Typography>
        <Typography sx={{ whiteSpace: 'pre-wrap' }}>{run.question_text}</Typography>
        <Typography sx={{ whiteSpace: 'pre-wrap', mb: 2 }}>{run.candidate_response || label('noWrittenResponse')}</Typography>
        <Button disabled={submitting} onClick={() => { setModel(run.model); setPrompt(run.system_prompt); }}>{label('reuse')}</Button>
        <Box component="details" sx={{ my: 2 }}><summary>{label('savedPrompt')}</summary>
          <Box component="pre" sx={{ whiteSpace: 'pre-wrap' }}>{run.system_prompt}</Box>
        </Box>
        <Box component="details"><summary>{label('renderedPrompt')}</summary>
          <Box component="pre" sx={{ whiteSpace: 'pre-wrap' }}>{run.rendered_prompt || '—'}</Box>
        </Box>
      </Paper>}
    </Stack>
  );
};

export default EvaluationLab;
